import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { blogPosts } from '@/data/blogPosts';

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? blogPosts.find((p) => p.id === slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label={post.title}>
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-16 sm:py-20">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to blog
              </Link>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {post.category}
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-2 mb-4 leading-[1.15] tracking-tight text-foreground">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
                <span>{post.readTime}</span>
              </div>
            </motion.div>
          </div>
        </section>
        <article className="container mx-auto px-4 py-12 sm:py-16 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-neutral dark:prose-invert max-w-none"
          >
            <p className="text-xl text-muted-foreground leading-relaxed">
              {post.excerpt}
            </p>
            <p className="mt-6 text-foreground leading-relaxed">
              This is a sample blog post. Full content would be rendered here—articles on AI coaching,
              micro learning, soft skills, and career growth. For now, this page confirms the link works
              and matches the design system.
            </p>
          </motion.div>
        </article>
      </div>
      <Footer />
    </main>
  );
};
